import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { scrollToDonationBox } from '../../../utils/helpers/scroll';
import { breakpoints, colors, spacing } from '../../../utils/styleguide';
import GhostButton from '../buttons/GhostButton';
import PrimaryButton from '../buttons/PrimaryButton';
import { DONATION_BOX_SELECTOR } from './constants';

const MobileFixed = styled.div<{ show: boolean }>`
  position: fixed;
  ${({ show }) =>
    !show &&
    css`
      display: none;
    `};

  bottom: 0;
  left: 0;
  width: 100vw;
  box-sizing: border-box;
  padding: 28px ${spacing[2]}px ${spacing[1]}px;

  background-image: linear-gradient(
    to bottom,
    rgba(255, 255, 255, 0) 0px,
    ${colors.white} 16px,
    ${colors.white}
  );
  ${breakpoints.tablet} {
    display: none;
  }
`;

const Buttons = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
`;

const Footer = styled.div`
  margin-top: ${spacing[2]}px;
`;

export type Props = {
  show: boolean;
  amount?: string;
  onDonate: () => void;
  disabled?: boolean;
  donateText: string;
  changeAmountText?: string;
  // Needs to use `any` since we don't know the type for the props of the component that will be ButtonsFooter
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  ButtonsFooter?: (props: any) => JSX.Element;
  loading?: boolean;
};

const FixedMobileButton = ({
  show,
  amount,
  onDonate,
  disabled,
  donateText,
  changeAmountText,
  ButtonsFooter,
  loading,
}: Props) => {
  const [clientMounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  const component = (
    <MobileFixed show={show} role="region" aria-label="Donation actions">
      <Buttons>
        <PrimaryButton
          variant="solid"
          label={`${donateText} ${amount ? amount : ''}`}
          onClick={onDonate}
          disabled={disabled}
          loading={loading}
          css={
            changeAmountText
              ? css`
                  margin: 0;
                  margin-right: ${spacing[1]}px;
                  font-size: 14px;
                `
              : css`
                  width: 100%;
                  font-size: 14px;
                  margin: 0 8px;
                `
          }
        />
        {changeAmountText && (
          <GhostButton
            color={colors.black}
            underlineColor={colors.yellow}
            href={DONATION_BOX_SELECTOR}
            css={css`
              margin-left: ${spacing[1]}px;
            `}
            label={changeAmountText}
            onClick={scrollToDonationBox}
          />
        )}
      </Buttons>
      {ButtonsFooter && (
        <Footer>
          <ButtonsFooter />
        </Footer>
      )}
    </MobileFixed>
  );

  if (!clientMounted || process.env.NODE_ENV === 'test') {
    return component;
  }

  return createPortal(component, document.getElementById('react-portal') || document.body);
};

export default FixedMobileButton;
